import { NotificationHandler } from '@sit/notifications-client';
import { NotificationPayload, NotificationTopic } from '@sit/notifications-shared';
import isEqual from 'lodash/isEqual';
import { use, useCallback, useEffect, useState } from 'react';
import { NotificationHandlerContext } from '../NotificationHandlerContext';

const handleNotificationMatch = (
  notification: NotificationPayload,
  topic: NotificationTopic,
  params: NotificationPayload['data'] | null = null,
): NotificationPayload | null => {
  const topicMatch = notification.topic === topic;

  const { data } = notification;
  const paramsMatch = !params ? true : data && Object.keys(params).every((key) => isEqual(data[key], params[key]));

  return topicMatch && paramsMatch ? notification : null;
};

export const useOnLatestNotification = (topic: NotificationTopic, handler: NotificationHandler) => {
  const { service: notificationHandlerService } = use(NotificationHandlerContext);

  return useEffect(() => notificationHandlerService.addHandler(topic, handler), [topic, handler, notificationHandlerService]);
};

export function useLatestNotification(
  topic: NotificationTopic,
  params?: NotificationPayload['data'],
  subscribeToNotification?: NotificationHandler,
): NotificationPayload | undefined {
  const { service: notificationHandlerService } = use(NotificationHandlerContext);
  const [notification, setNotification] = useState<NotificationPayload | undefined>(undefined);

  const handleNotification = useCallback(
    (incomingNotification: NotificationPayload): void => {
      if (handleNotificationMatch(incomingNotification, topic, params)) {
        subscribeToNotification?.(incomingNotification);
        setNotification(incomingNotification);
      }
    },
    [params, topic, subscribeToNotification],
  );

  useEffect(
    () => notificationHandlerService.addHandler(topic, handleNotification),
    [topic, handleNotification, notificationHandlerService],
  );

  return notification;
}
